/**
 * Pricing Calculator Service
 *
 * Pure pricing engine for the form builder's "pricing-calculator" section type.
 * Shared by the builder editor preview, the client project wizard, and the
 * project brief renderer so all three always compute identical totals.
 *
 * Schema notes (Firestore-safe — no nested arrays):
 * - Stepper tier ranges are { from, to } objects; to === null means open-ended.
 * - The day-rate matrix is object-keyed: rates[styleValue][durationValue].
 */

import { convertTimestampToISO } from '@/services/dateService'

/**
 * How long a quoted price snapshot stays valid, measured from the order
 * draft's creation. After this window the wizard re-snapshots the calculator
 * against the latest questionnaire + current rates. Single source of truth for
 * the validity rule (wizard disclaimer, quote PDF, and expiry check).
 */
export const QUOTE_VALIDITY_DAYS = 30

/**
 * Resolve the date a quote stays valid through: createdAt + QUOTE_VALIDITY_DAYS.
 * Accepts a Firestore Timestamp, ISO string, millis, or Date; defaults to now.
 *
 * @returns {Date}
 */
export function getQuoteValidThrough(createdAt) {
  // Default to now so an absent/unparseable date reads as fresh, never expired.
  // (convertTimestampToISO returns null on failure, and new Date(null) is 1970.)
  let base = new Date()
  if (createdAt) {
    const iso = convertTimestampToISO(createdAt)
    const parsed = iso ? new Date(iso) : null
    if (parsed && !Number.isNaN(parsed.getTime())) base = parsed
  }
  const through = new Date(base)
  through.setDate(through.getDate() + QUOTE_VALIDITY_DAYS)
  return through
}

/**
 * Whether a quote snapshot created at `createdAt` has passed its validity
 * window (i.e. now is on/after day 31). Missing dates are treated as fresh.
 *
 * @returns {boolean}
 */
export function isQuoteExpired(createdAt) {
  if (!createdAt) return false
  return new Date() > getQuoteValidThrough(createdAt)
}

/**
 * Default calculator configuration (seeded when a pricing-calculator
 * section is added in the form builder). Matches the validated prototype.
 */
export const DEFAULT_CALCULATOR_CONFIG = {
  version: 1,
  currency: 'USD',
  blocks: [
    {
      id: 'blk_projectname',
      type: 'text',
      key: 'projectName',
      label: 'What should we call this project?',
      sublabel: '',
      placeholder: 'e.g. Q3 executive welcome video'
    },
    {
      id: 'blk_fullvideos',
      type: 'stepper',
      key: 'fullVideos',
      label: 'Full Video(s)',
      sublabel: 'Original edit, built from scratch · :90 seconds–5 minutes',
      min: 0,
      default: 1,
      pricing: {
        mode: 'tiered',
        flatBase: null,
        tiers: [
          { id: 'tier_fv_1', from: 1, to: 2, unitPrice: 2500 },
          { id: 'tier_fv_2', from: 3, to: 5, unitPrice: 2000 },
          { id: 'tier_fv_3', from: 6, to: null, unitPrice: 1500 }
        ]
      }
    },
    {
      id: 'blk_socialcuts',
      type: 'stepper',
      key: 'socialCuts',
      label: 'Social Cut(s)',
      sublabel: 'A simple, single-message clip — not a creative build · :15 seconds–1 minute',
      min: 0,
      default: 0,
      pricing: {
        mode: 'tiered',
        flatBase: { amount: 1500, coversUpTo: 3 },
        tiers: [
          { id: 'tier_sc_1', from: 4, to: 10, unitPrice: 450 },
          { id: 'tier_sc_2', from: 11, to: 20, unitPrice: 350 },
          { id: 'tier_sc_3', from: 21, to: null, unitPrice: 250 }
        ]
      }
    },
    {
      id: 'blk_footageonly',
      type: 'toggle',
      key: 'footageOnly',
      label: 'I just want the footage',
      sublabel: 'Raw files only — no edited deliverables',
      default: false,
      clearsSteppers: true,
      unlocksProduction: true
    },
    {
      id: 'blk_style',
      type: 'choice',
      key: 'style',
      label: 'How polished should it look?',
      options: [
        {
          id: 'professional',
          label: 'Professional',
          sublabel: 'Polished, Cinematic Results',
          spec: '2–3 person crew + 2-camera production kit'
        },
        {
          id: 'organic',
          label: 'Organic',
          sublabel: 'Authentic, Social-First Results',
          spec: '1 person crew + 1 camera & mic'
        }
      ]
    },
    {
      id: 'blk_duration',
      type: 'choice',
      key: 'duration',
      label: 'How long do you need us on site?',
      options: [
        { id: '2', label: '2 hours' },
        { id: '4', label: '4 hours' },
        { id: '8', label: '8 hours' }
      ],
      // Each entry is a production day with its own duration selection
      repeatable: true,
      addLabel: 'Add another day',
      itemLabel: 'Day',
      showDayRate: true,
      hint: {
        text: 'Professional interview setups require 1 hour of setup time.',
        visibleWhen: { blockKey: 'style', equals: 'professional' }
      }
    }
  ],
  dayRateMatrix: {
    styleBlockKey: 'style',
    durationBlockKey: 'duration',
    rates: {
      professional: { 2: 3500, 4: 5000, 8: 7500 },
      organic: { 2: 1500, 4: 2500, 8: 4000 }
    }
  }
}

/**
 * Cost for a tiered stepper given a unit count.
 *
 * With flatBase: the flat amount covers units 1..coversUpTo (charged once when
 * count > 0); units beyond that are priced cumulatively per-unit by tier.
 * Without flatBase: every unit is priced per-unit by the tier its index falls in.
 *
 * @param {number} count - Number of units selected
 * @param {Object} pricing - { mode, flatBase, tiers: [{ from, to, unitPrice }] }
 * @returns {number} Total cost for this stepper
 */
function calcStepperCost(count, pricing) {
  const units = Math.max(0, Math.floor(Number(count) || 0))
  if (units === 0 || !pricing) return 0

  let cost = 0
  let firstUnpricedUnit = 1

  if (pricing.flatBase && pricing.flatBase.amount > 0) {
    cost += Number(pricing.flatBase.amount) || 0
    firstUnpricedUnit = (Number(pricing.flatBase.coversUpTo) || 0) + 1
  }

  const tiers = Array.isArray(pricing.tiers) ? pricing.tiers : []
  for (let unit = firstUnpricedUnit; unit <= units; unit++) {
    const tier = tiers.find(t =>
      unit >= (Number(t.from) || 0) &&
      (t.to === null || t.to === undefined || t.to === '' || unit <= Number(t.to))
    )
    if (tier) {
      cost += Number(tier.unitPrice) || 0
    }
  }

  return cost
}

/**
 * Normalize a choice value to an array of entries.
 * Repeatable blocks store arrays; legacy/single blocks store scalars.
 */
export function normalizeChoiceEntries(value) {
  if (Array.isArray(value)) return value
  return value === null || value === undefined ? [] : [value]
}

/**
 * Look up the day rate for a single style/duration pair.
 */
export function calcDayRateForEntry(matrix, styleValue, durationValue) {
  return Number(matrix?.rates?.[styleValue]?.[durationValue]) || 0
}

/**
 * Total day rate for the current selections. The duration block may be
 * repeatable (multiple production days) - each entry adds its day rate.
 *
 * @param {Object} matrix - { styleBlockKey, durationBlockKey, rates }
 * @param {Object} values - Current selection values keyed by block key
 * @returns {number} Summed day rate, or 0 if not resolvable
 */
export function calcDayRate(matrix, values) {
  if (!matrix || !values) return 0
  const styleValue = normalizeChoiceEntries(values[matrix.styleBlockKey])[0]
  const durations = normalizeChoiceEntries(values[matrix.durationBlockKey])
  return durations.reduce(
    (total, duration) => total + calcDayRateForEntry(matrix, styleValue, duration),
    0
  )
}

/**
 * Whether a block's conditional hint is visible for the current values.
 *
 * @param {Object} hint - { text, visibleWhen: { blockKey, equals } | null }
 * @param {Object} values - Current selection values keyed by block key
 * @returns {boolean}
 */
export function isHintVisible(hint, values) {
  if (!hint?.text) return false
  if (!hint.visibleWhen?.blockKey) return true
  return normalizeChoiceEntries(values?.[hint.visibleWhen.blockKey]).includes(
    hint.visibleWhen.equals
  )
}

/**
 * Find a choice block's option object by value.
 */
function getOption(calculator, blockKey, value) {
  const block = calculator?.blocks?.find(b => b.key === blockKey)
  return block?.options?.find(o => o.id === value) || null
}

/**
 * Find a choice block's option label by value (falls back to the raw value).
 */
function getOptionLabel(calculator, blockKey, value) {
  return getOption(calculator, blockKey, value)?.label || value || ''
}

/**
 * Resolve the selected production-style label (e.g. "Premium") for display.
 * Empty string when there is no style block or nothing is selected.
 *
 * @returns {string}
 */
export function getCalculatorStyleLabel(calculator, values) {
  const styleKey = calculator?.dayRateMatrix?.styleBlockKey
  if (!styleKey) return ''
  const styleValue = normalizeChoiceEntries(values?.[styleKey])[0]
  return getOptionLabel(calculator, styleKey, styleValue)
}

/**
 * Build richer, document-friendly line items for the quote PDF: each has a
 * bold `title` plus an explanatory `detail` drawn from the calculator's own
 * labels/sublabels (rather than the terse parenthetical form used in-app).
 * Each item is tagged `kind` ('production' for scheduled days, 'deliverable'
 * for priced steppers) so consumers can split them by type.
 *
 * @returns {Array<{kind: string, title: string, detail: string, amount: number}>}
 */
export function buildQuotePdfLineItems(calculator, values) {
  const items = []
  if (!calculator || !values) return items

  const join = (parts) => parts.filter(Boolean).join(' · ')

  // Production days (style × duration matrix); one line per scheduled day
  const matrix = calculator.dayRateMatrix
  if (matrix) {
    const styleValue = normalizeChoiceEntries(values[matrix.styleBlockKey])[0]
    const durations = normalizeChoiceEntries(values[matrix.durationBlockKey])
    const styleOption = getOption(calculator, matrix.styleBlockKey, styleValue)
    const durationBlock = calculator.blocks?.find(b => b.key === matrix.durationBlockKey)
    const itemLabel = durationBlock?.itemLabel || 'Day'

    durations.forEach((duration, index) => {
      const dayRate = calcDayRateForEntry(matrix, styleValue, duration)
      if (dayRate <= 0) return
      const durationOption = getOption(calculator, matrix.durationBlockKey, duration)
      items.push({
        kind: 'production',
        title: durations.length > 1
          ? `Production ${itemLabel} ${index + 1}`
          : `Production ${itemLabel.toLowerCase()}`,
        detail: join([
          styleOption?.label && `${styleOption.label} style`,
          durationOption?.label,
          durationOption?.sublabel
        ]),
        amount: dayRate
      })
    })
  }

  // Priced stepper blocks
  for (const block of calculator.blocks || []) {
    if (block.type !== 'stepper' || !block.pricing) continue
    const count = Math.max(0, Math.floor(Number(values[block.key]) || 0))
    const cost = calcStepperCost(count, block.pricing)
    if (count <= 0 || cost <= 0) continue
    items.push({
      kind: 'deliverable',
      title: block.label,
      detail: join([`Qty ${count}`, block.sublabel]),
      amount: cost
    })
  }

  return items
}

/**
 * Compute the full price breakdown for a calculator config + selection values.
 *
 * @param {Object} calculator - Calculator config (blocks + dayRateMatrix)
 * @param {Object} values - Current selection values keyed by block key
 * @returns {{ lineItems: Array<{label: string, amount: number}>, total: number, currency: string }}
 */
export function computeCalculatorTotals(calculator, values) {
  const lineItems = []
  let total = 0

  if (!calculator || !values) {
    return { lineItems, total, currency: calculator?.currency || 'USD' }
  }

  // Production day rate (style × duration matrix); the duration block may be
  // repeatable - one line item per production day
  const matrix = calculator.dayRateMatrix
  if (matrix) {
    const styleValue = normalizeChoiceEntries(values[matrix.styleBlockKey])[0]
    const durations = normalizeChoiceEntries(values[matrix.durationBlockKey])
    const styleLabel = getOptionLabel(calculator, matrix.styleBlockKey, styleValue)
    const durationBlock = calculator.blocks?.find(b => b.key === matrix.durationBlockKey)
    const itemLabel = durationBlock?.itemLabel || 'Day'

    durations.forEach((duration, index) => {
      const dayRate = calcDayRateForEntry(matrix, styleValue, duration)
      if (dayRate > 0) {
        const durationLabel = getOptionLabel(calculator, matrix.durationBlockKey, duration)
        const label = durations.length > 1
          ? `Production ${itemLabel} ${index + 1} (${styleLabel}, ${durationLabel})`
          : `Production (${styleLabel}, ${durationLabel})`
        lineItems.push({ label, amount: dayRate })
        total += dayRate
      }
    })
  }

  // Priced stepper blocks
  for (const block of calculator.blocks || []) {
    if (block.type !== 'stepper' || !block.pricing) continue
    const count = Math.max(0, Math.floor(Number(values[block.key]) || 0))
    const cost = calcStepperCost(count, block.pricing)
    if (count > 0 && cost > 0) {
      lineItems.push({
        label: `${block.label} (${count})`,
        amount: cost
      })
      total += cost
    }
  }

  return { lineItems, total, currency: calculator.currency || 'USD' }
}

/**
 * Build the initial selection values for a calculator config (block defaults).
 *
 * @param {Object} calculator - Calculator config
 * @returns {Object} values keyed by block key
 */
export function initCalculatorSelections(calculator) {
  const values = {}
  for (const block of calculator?.blocks || []) {
    if (block.type === 'text') {
      values[block.key] = ''
    } else if (block.type === 'stepper') {
      values[block.key] = Number.isFinite(Number(block.default)) ? Number(block.default) : 0
    } else if (block.type === 'toggle') {
      values[block.key] = !!block.default
    } else if (block.type === 'choice') {
      // Choices start unanswered (null) unless the config sets an explicit
      // default - no first-option fallback, the user makes the call
      const defaultValue = block.default ?? null
      // Repeatable choices (e.g., production days) store an array of entries
      values[block.key] = block.repeatable ? [defaultValue] : defaultValue
    }
  }
  return values
}

/**
 * What the currently-checked toggle blocks do to the rest of the calculator.
 * Both effects default ON (a toggle with neither is inert), and are configured
 * per block in the calculator editor:
 * - clearsSteppers: zero out (and lock) every stepper block
 * - unlocksProduction: satisfy the "add at least one item" gate on its own
 *
 * Shared by the gating cascade and the renderer so lock state and computed
 * values can never disagree.
 *
 * @returns {{ clearsSteppers: boolean, unlocksProduction: boolean }}
 */
export function getToggleEffects(calculator, values) {
  const active = (calculator?.blocks || []).filter(
    (b) => b.type === 'toggle' && values?.[b.key]
  )
  return {
    clearsSteppers: active.some((b) => b.clearsSteppers !== false),
    unlocksProduction: active.some((b) => b.unlocksProduction !== false)
  }
}

/**
 * Progressive-gating cascade for calculator selections. The day-rate matrix
 * encodes the dependency chain: deliverable steppers unlock the style block,
 * an answered style unlocks the duration block. Selections that fall behind
 * a lock are cleared so a hidden choice can never keep charging (e.g. items
 * dropped back to 0 must drop the day rate too).
 *
 * A checked toggle block ("I just want the footage") is the escape hatch: it
 * zeroes the deliverable steppers and unlocks production on its own, so a
 * footage-only shoot can still be priced and booked.
 *
 * @param {Object} calculator - Calculator config (blocks + dayRateMatrix)
 * @param {Object} values - Selection values keyed by block key
 * @returns {Object} New values object with locked selections cleared
 */
export function applyCalculatorGating(calculator, values) {
  const updated = { ...(values || {}) }
  const blocks = calculator?.blocks || []

  const effects = getToggleEffects(calculator, updated)
  if (effects.clearsSteppers) {
    for (const block of blocks) {
      if (block.type === 'stepper') updated[block.key] = 0
    }
  }

  const matrix = calculator?.dayRateMatrix
  if (!matrix) return updated

  const stepperHasItems = effects.unlocksProduction || blocks.some(
    (b) => b.type === 'stepper' && Number(updated[b.key]) > 0
  )
  const clearedValue = (key) => {
    const block = blocks.find((b) => b.key === key)
    return block?.repeatable ? [null] : null
  }

  if (!stepperHasItems && matrix.styleBlockKey) {
    updated[matrix.styleBlockKey] = clearedValue(matrix.styleBlockKey)
  }

  const styleAnswered = normalizeChoiceEntries(updated[matrix.styleBlockKey]).some(
    (e) => e !== null && e !== undefined && e !== ''
  )
  if ((!stepperHasItems || !styleAnswered) && matrix.durationBlockKey) {
    updated[matrix.durationBlockKey] = clearedValue(matrix.durationBlockKey)
  }

  return updated
}

/**
 * Format an amount for display (e.g. 14350 -> "$14,350").
 */
export function formatCalculatorAmount(amount, _currency = 'USD') {
  return `$${Math.round(Number(amount) || 0).toLocaleString('en-US')}`
}

/**
 * Format a tier range for display - null/empty "to" means open-ended (e.g. "6+").
 */
export function formatTierRange(tier) {
  if (!tier) return ''
  const from = Number(tier.from) || 0
  return tier.to === null || tier.to === undefined || tier.to === ''
    ? `${from}+`
    : `${from}–${Number(tier.to)}`
}

/**
 * Build an MSA custom-pricing object from a calculator config, prefilled with
 * the calculator's current default rates. This is the editable template shown
 * on the MSA contract form; the admin edits values, never structure.
 *
 * Shape: { version, dayRates: { styleId: { durationId: rate } },
 *          steppers: { blockKey: pricing } }
 */
export function extractMsaPricingTemplate(calculator) {
  const template = { version: 1, dayRates: null, steppers: {} }
  if (!calculator) return template

  if (calculator.dayRateMatrix?.rates) {
    template.dayRates = JSON.parse(JSON.stringify(calculator.dayRateMatrix.rates))
  }

  for (const block of calculator.blocks || []) {
    if (block.type === 'stepper' && block.pricing) {
      template.steppers[block.key] = JSON.parse(JSON.stringify(block.pricing))
    }
  }

  return template
}

/**
 * Overlay a stored MSA custom-pricing object onto a fresh template derived
 * from the current calculator config. Keeps the template's structure (so the
 * editor always reflects the live calculator) while preserving any stored
 * values whose keys still match. Returns drift warnings for keys that no
 * longer line up.
 */
export function mergeMsaPricingIntoTemplate(template, stored) {
  const warnings = []
  const merged = JSON.parse(JSON.stringify(template))
  if (!stored) return { pricing: merged, warnings }

  // Day rates: per-cell overlay so renamed options keep defaults (with warning)
  if (stored.dayRates && merged.dayRates) {
    for (const [styleId, durations] of Object.entries(stored.dayRates)) {
      if (!merged.dayRates[styleId]) {
        warnings.push(`Custom day rates for "${styleId}" no longer match a calculator option`)
        continue
      }
      for (const [durationId, rate] of Object.entries(durations || {})) {
        if (durationId in merged.dayRates[styleId]) {
          merged.dayRates[styleId][durationId] = rate
        } else {
          warnings.push(`Custom day rate "${styleId}/${durationId}" no longer matches a calculator option`)
        }
      }
    }
  }

  // Steppers: replace pricing wholesale when the block key still exists
  for (const [key, pricing] of Object.entries(stored.steppers || {})) {
    if (key in merged.steppers) {
      merged.steppers[key] = JSON.parse(JSON.stringify(pricing))
    } else {
      warnings.push(`Custom pricing for "${key}" no longer matches a calculator block`)
    }
  }

  return { pricing: merged, warnings }
}

/**
 * Apply an MSA custom-pricing override to a calculator config.
 * Returns a merged clone plus drift warnings; the original is untouched.
 *
 * - Stepper pricing is replaced wholesale per matching block key.
 * - Day rates are overlaid per cell, so cells the override doesn't cover
 *   keep the calculator's defaults (never a silent $0 lookup).
 */
export function applyMsaPricingOverride(calculator, msaPricing) {
  const warnings = []
  if (!calculator) return { calculator, warnings }

  const merged = JSON.parse(JSON.stringify(calculator))
  if (!msaPricing) return { calculator: merged, warnings }

  for (const block of merged.blocks || []) {
    if (block.type === 'stepper' && msaPricing.steppers?.[block.key]) {
      block.pricing = JSON.parse(JSON.stringify(msaPricing.steppers[block.key]))
    }
  }

  // Warn about override stepper keys with no matching block (structure drift)
  for (const key of Object.keys(msaPricing.steppers || {})) {
    const hasBlock = (merged.blocks || []).some((b) => b.type === 'stepper' && b.key === key)
    if (!hasBlock) {
      warnings.push(`Custom pricing for "${key}" has no matching calculator block`)
    }
  }

  if (msaPricing.dayRates && merged.dayRateMatrix?.rates) {
    for (const [styleId, durations] of Object.entries(msaPricing.dayRates)) {
      if (!merged.dayRateMatrix.rates[styleId]) {
        warnings.push(`Custom day rates for "${styleId}" have no matching calculator option`)
        continue
      }
      for (const [durationId, rate] of Object.entries(durations || {})) {
        if (durationId in merged.dayRateMatrix.rates[styleId]) {
          merged.dayRateMatrix.rates[styleId][durationId] = Number(rate) || 0
        } else {
          warnings.push(`Custom day rate "${styleId}/${durationId}" has no matching calculator option`)
        }
      }
    }
  }

  return { calculator: merged, warnings }
}

// Agency markup is the THIRD overlay in the snapshot chain:
//   base rate card -> applyMsaPricingOverride -> applyAgencyMarkup
// It lives in ./agencyMarkup.js, kept dependency-free so its money math can be
// checked by scripts/checkAgencyMarkup.mjs under plain node.

/**
 * Apply an MSA custom-pricing override to freshly generated brief sections
 * (mutates calculator + re-inits selections on pricing-calculator sections).
 * Use only on fresh sections - this resets any user selections.
 */
export function applyMsaOverrideToBriefSections(sections, msaPricing) {
  const warnings = []
  if (!Array.isArray(sections) || !msaPricing) return { sections, warnings }

  const updated = sections.map((section) => {
    if (section?.type !== 'pricing-calculator' || !section.calculator) {
      return section
    }

    const result = applyMsaPricingOverride(section.calculator, msaPricing)
    warnings.push(...result.warnings)

    const values = initCalculatorSelections(result.calculator)
    const totals = computeCalculatorTotals(result.calculator, values)

    return {
      ...section,
      calculator: result.calculator,
      selections: {
        values,
        lineItems: totals.lineItems,
        total: totals.total,
        currency: totals.currency,
        computedAt: null
      }
    }
  })

  return { sections: updated, warnings }
}
